import { ChangeEvent, useState } from 'react'
import * as yup from 'yup'
import cn from 'classnames'
import { useToasts } from 'react-toast-notifications'
import { Formik, Form, FormikHelpers } from 'formik-latest'

import TextArea from './TextArea'
import ConsolePetitionActionButton from './ActionButton'
import { useAddCommentMutation } from 'backoffice/components/Drafts/useDraftsQuery'

import styles from './ActionForm.module.scss'

interface CommentValues {
  comment: string
}

interface CommentFormCustomProps {
  isSubmitting: boolean
  handleChange: (e: ChangeEvent<HTMLTextAreaElement>) => void
  values: CommentValues
  isValid: boolean
  maxCommentLength: number
}

const CommentFormCustom = (props: CommentFormCustomProps) => {
  const { isSubmitting, handleChange, values, isValid, maxCommentLength } = props

  const isDisabledSubmit = isSubmitting || !isValid || !values.comment.trim()
  const [commentCharacterCounter, setCommentCounterCharacter] = useState(0)

  const onChangeCommentHandler = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (!e) {
      return
    }
    setCommentCounterCharacter(e.target.value.length)
    handleChange(e)
  }

  return (
    <Form className={styles.actionForm}>
      <label className={styles.label} htmlFor="comment">
        Comment *
      </label>
      <TextArea
        id="comment"
        name="comment"
        className={styles.textArea}
        placeholder="Enter your comment"
        rows={4}
        maxLength={maxCommentLength}
        onChange={onChangeCommentHandler}
      />
      <span className={styles.commentCharacterCounter}>
        {commentCharacterCounter}/{maxCommentLength}
      </span>

      <ConsolePetitionActionButton
        className={cn(styles.submitButton, styles.addComment, { [styles.disabled]: isDisabledSubmit })}
        isDisabled={isDisabledSubmit}
        onClick={() => {}}
        type="submit"
      >
        {isSubmitting ? 'Adding Comment...' : 'Add Comment'}
      </ConsolePetitionActionButton>
    </Form>
  )
}

interface AddCommentFormProps {
  draftId: number
  handleClose: () => void
}

const AddCommentForm = ({ draftId, handleClose }: AddCommentFormProps) => {
  const mutation = useAddCommentMutation()
  const { addToast } = useToasts()

  const MAX_COMMENT_LENGTH = 1000

  const validationSchema = yup.object({
    comment: yup
      .string()
      .required('Comment is required')
      .max(MAX_COMMENT_LENGTH, `This field can contain a maximum of ${MAX_COMMENT_LENGTH} characters`),
  })

  const initialValues: CommentValues = {
    comment: '',
  }

  const onSubmitHandler = async (values: CommentValues, { setSubmitting }: FormikHelpers<CommentValues>) => {
    try {
      await mutation.mutateAsync({
        draftId,
        comment: values.comment,
      })
      addToast('Comment added successfully', { appearance: 'success', autoDismiss: true })
      handleClose()
    } catch {
      addToast('There was a problem adding the comment', { appearance: 'error', autoDismiss: true })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmitHandler}
      validateOnBlur
      validateOnChange
    >
      {(props) => <CommentFormCustom {...props} maxCommentLength={MAX_COMMENT_LENGTH} />}
    </Formik>
  )
}

export default AddCommentForm
